import { useMemo, useRef, useState } from 'react';
import * as yup from 'yup';
import LoadingScreen from '../../../components/admin/common/OZLoadingScreen/OZLoadingScreen';
import { Formik, Field } from 'formik';
import OZInput from '../../../components/admin/common/OZInput';
import OZDialog from '../../../components/admin/common/OZDialog';
import { Autocomplete, Chip, Divider, Grid, TextField, Typography } from '@mui/material';
import OZSelect from '../../../components/admin/common/OZSelect';
import { DropzoneAreaBase } from "mui-file-dropzone";
import './Integration.css';
import { showErrorNotification, showInfoNotification, showSuccessNotification, showWarningNotification } from '../../../components/admin/common/NotiService';
import { saveSSOLoginWithLDAP, saveSSOLoginWithOAuth2, saveSSOLoginWithSAML } from '../../../services/api-service';
import OZFCheckbox from '../../../components/admin/common/OZFCheckbox';

const optionalOAuth2Field = yup.string().nullable().notRequired();

const optionalOAuth2Url = (label) => yup
    .string()
    .transform((value, originalValue) =>
        typeof originalValue === 'string' && originalValue.trim() === '' ? null : value
    )
    .nullable()
    .url(`${label} must be a valid URL`)
    .notRequired();

const optionalOAuth2Scopes = yup.array().of(yup.string().trim()).nullable().notRequired();

const normalizeScopeValues = (scopeValue) => {
    const values = Array.isArray(scopeValue) ? scopeValue : [scopeValue];
    const normalizedValues = values.flatMap((value) =>
        typeof value === 'string' ? value.trim().split(/\s+/).filter(Boolean) : []
    );

    return [...new Set(normalizedValues)];
};

const ScopeChipInput = ({ value, onChange, onBlur, error, helperText }) => {
    const [inputValue, setInputValue] = useState('');
    const scopeValues = normalizeScopeValues(value);

    const commitInputValue = (nextInputValue) => {
        const nextScopes = normalizeScopeValues([...scopeValues, nextInputValue]);
        if (nextScopes.length !== scopeValues.length || nextScopes.some((scope, index) => scope !== scopeValues[index])) {
            onChange(nextScopes);
        }
        setInputValue('');
    };

    return (
        <>
            <Typography
                fontSize='12px'
                fontWeight={400}
                lineHeight='14px'
                marginBottom='6px'
                marginLeft='1px'
                color='#47586E'
            >
                Scope
            </Typography>
            <Autocomplete
                multiple
                freeSolo
                forcePopupIcon={false}
                options={[]}
                value={scopeValues}
                inputValue={inputValue}
                onInputChange={(event, nextInputValue) => setInputValue(nextInputValue)}
                onChange={(event, nextValues) => onChange(normalizeScopeValues(nextValues))}
                onBlur={() => {
                    if (inputValue.trim()) {
                        commitInputValue(inputValue);
                    }
                    onBlur();
                }}
                renderTags={(tagValues, getTagProps) =>
                    tagValues.map((scope, index) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        return (
                            <Chip
                                key={key || scope}
                                label={scope}
                                {...tagProps}
                                sx={{
                                    height: '30px',
                                    borderRadius: '15px',
                                    backgroundColor: '#EEF0F4',
                                    color: '#212121',
                                    fontSize: '14px',
                                    fontWeight: 400,
                                    '& .MuiChip-label': {
                                        paddingLeft: '12px',
                                        paddingRight: '6px',
                                    },
                                    '& .MuiChip-deleteIcon': {
                                        color: '#7C878B',
                                        fontSize: '18px',
                                        marginRight: '7px',
                                        '&:hover': {
                                            color: '#47586E',
                                        },
                                    },
                                }}
                            />
                        );
                    })
                }
                renderInput={(params) => (
                    <TextField
                        {...params}
                        placeholder={scopeValues.length > 0 ? 'Add scope' : 'Type a scope and press Enter'}
                        error={error}
                        helperText={helperText}
                        inputProps={{
                            ...params.inputProps,
                            'aria-label': 'Scope',
                        }}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                                event.preventDefault();
                                event.stopPropagation();
                                if (inputValue.trim()) {
                                    commitInputValue(inputValue);
                                }
                            }
                        }}
                    />
                )}
                sx={{
                    '& .MuiOutlinedInput-root': {
                        minHeight: '44px',
                        alignItems: 'center',
                        gap: '2px',
                        padding: '5px 10px !important',
                        borderRadius: '8px',
                        backgroundColor: '#FFFFFF',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#D6D6D7',
                    },
                    '& .MuiAutocomplete-input': {
                        minWidth: '110px !important',
                        padding: '4px 2px !important',
                        fontSize: '16px',
                        fontWeight: 400,
                    },
                }}
            />
        </>
    );
};

const SSOLogin = (mainprops) => {
    const validationSchema = useMemo(
        () =>
            yup.object().shape({
                authType: yup
                    .string()
                    .required("Category is Required"),
                authConfig: yup
                    .string()
                    .when("authType", {
                        is: (authType) => (authType === "SAML"),
                        then: yup
                            .string()
                            .required("IpEntityId is Required"),
                        otherwise: yup.string().notRequired().nullable(),
                    }),
                url: yup
                    .string()
                    .when("authType", {
                        is: (authType) => (authType === "LDAP"),
                        then: yup
                            .string()
                            .required("Connection Url is Required"),
                        otherwise: yup.string().notRequired().nullable(),
                    }),
                port: yup
                    .string()
                    .when("authType", {
                        is: (authType) => (authType === "LDAP"),
                        then: yup
                            .string()
                            .required("Connection Port is Required"),
                        otherwise: yup.string().notRequired().nullable(),
                    }),
                userDn: yup
                    .string()
                    .when("authType", {
                        is: (authType) => (authType === "LDAP"),
                        then: yup
                            .string()
                            .required("User DN is Required"),
                        otherwise: yup.string().notRequired().nullable(),
                    }),
                domain: yup
                    .string()
                    .when("authType", {
                        is: (authType) => (authType === "LDAP"),
                        then: yup
                            .string()
                            .required("Domain is Required"),
                        otherwise: yup.string().notRequired().nullable(),
                    }),
                dc: yup
                    .string()
                    .when("authType", {
                        is: (authType) => (authType === "LDAP"),
                        then: yup
                            .string()
                            .required("DC is Required"),
                        otherwise: yup.string().notRequired().nullable(),
                    }),
                clientId: optionalOAuth2Field,
                clientSecret: optionalOAuth2Field,
                scope: optionalOAuth2Scopes,
                authorizationUri: optionalOAuth2Url("Authorization URI"),
                tokenUri: optionalOAuth2Url("Token URI"),
                userInfoUri: optionalOAuth2Url("User Info URI"),
                userNameAttributeName: optionalOAuth2Field,
                jwkSetUri: optionalOAuth2Url("JWK Set URI"),
            }),
        []
    );

    const [loading, setLoading] = useState(false);
    const submittingRef = useRef(false);

    function UploadIcon() {
        return (
            <div style={{ paddingTop: '12px', float: 'right', paddingRight: '15px' }}>
                <img alt='edit' src={`${process.env.PUBLIC_URL}/icons/upload_data_reverse.svg`}></img>
            </div>
        )
    }

    function checkVariant(message, variant) {
        if (variant === "success") {
            showSuccessNotification(`${(variant[0].toUpperCase() + variant.slice(1))}: ${message}`);
        }
        else if (variant === "error") {
            showErrorNotification(`${(variant[0].toUpperCase() + variant.slice(1))}: ${message}`);
        }
        else {
            showInfoNotification(`${(variant[0].toUpperCase() + variant.slice(1))}: ${message}`);
        }
    }

    const DataForm = () => {

        let initData = {
            authType: mainprops.fixedAuthType || mainprops?.data?.authType || "SAML",
            authConfig: mainprops?.data?.authConfig || '',
            federationUrl: mainprops?.data?.federationUrl || '',
            authFile: mainprops?.data?.authFile || [],
            url: mainprops?.data?.url || '',
            port: mainprops?.data?.port || '',
            userDn: mainprops?.data?.userDn || '',
            domain: mainprops?.data?.domain || '',
            dc: mainprops?.data?.dc || '',
            isAllowAdmin: mainprops?.data?.isAllowAdmin || false,
            clientId: mainprops?.data?.clientId || '',
            clientSecret: mainprops?.data?.clientSecret || '',
            scope: normalizeScopeValues(mainprops?.data?.scope),
            authorizationUri: mainprops?.data?.authorizationUri || '',
            tokenUri: mainprops?.data?.tokenUri || '',
            userInfoUri: mainprops?.data?.userInfoUri || '',
            userNameAttributeName: mainprops?.data?.userNameAttributeName || '',
            jwkSetUri: mainprops?.data?.jwkSetUri || ''
        }

        const saveSsoLogin = async (values) => {
            if (values.authFile.length === 0 && values.federationUrl === "" && values.authType === "SAML") {
                showWarningNotification("FedeartionURL or File is required.")
            }
            else if (values.authFile.length > 0 && values.federationUrl !== "" && values.authType === "SAML") {
                showWarningNotification("you can give FederationURL or File , not both.")
            }
            else {
                if (submittingRef.current) {
                    return;
                }

                submittingRef.current = true;
                setLoading(true);
                try {
                    let response;

                    if (values.authType === "SAML") {
                        const formData = new FormData();
                        const authConfig = {
                            url: values.authConfig,
                            isAllowAdmin: values.isAllowAdmin || false
                        }
                        if (values.federationUrl !== "") {
                            authConfig["federationUrl"] = values.federationUrl
                        }

                        formData.append('data', JSON.stringify({
                            authType: values.authType,
                            authConfig: JSON.stringify(authConfig),
                        }));
                        if (values.authFile.length > 0) {
                            formData.append('file', values.authFile[0].file);
                        }
                        response = await saveSSOLoginWithSAML(formData);
                    }
                    else if (values.authType === "LDAP") {
                        const authConfig = {
                            url: values.url,
                            port: values.port,
                            userDn: values.userDn,
                            domain: values.domain,
                            dc: values.dc,
                            isAllowAdmin: values.isAllowAdmin || false
                        }
                        response = await saveSSOLoginWithLDAP({
                            authType: values.authType,
                            authConfig: JSON.stringify(authConfig),
                        });
                    }
                    else if (values.authType === "OAUTH2") {
                        const authConfig = {
                            clientId: values.clientId ?? '',
                            clientSecret: values.clientSecret ?? '',
                            scope: normalizeScopeValues(values.scope).join(' '),
                            authorizationUri: values.authorizationUri ?? '',
                            tokenUri: values.tokenUri ?? '',
                            userInfoUri: values.userInfoUri ?? '',
                            userNameAttributeName: values.userNameAttributeName ?? '',
                            jwkSetUri: values.jwkSetUri ?? '',
                        };
                        response = await saveSSOLoginWithOAuth2({
                            authType: "OAUTH2",
                            authConfig: JSON.stringify(authConfig),
                        });
                    }

                    if (response?.Status === "Success") {
                        showSuccessNotification("SSO Login details saved successfully")
                        mainprops.getUserIntegrationsData()
                        mainprops.onClickCancel()
                    }
                    else {
                        showErrorNotification(response?.Message || "Error saving SSOLogin details")
                    }
                }
                catch (error) {
                    console.error("Error saving SSO login details", error)
                    showErrorNotification("Error saving SSOLogin details");
                }
                finally {
                    submittingRef.current = false;
                    setLoading(false)
                }
            }
        }

        return (
            <div>
                <Formik
                    initialValues={initData}
                    validationSchema={validationSchema}
                    onSubmit={saveSsoLogin}
                >
                    {({ errors, handleSubmit, setFieldTouched, setFieldValue, touched, values }) => (
                        <div>
                            <form
                                onSubmit={handleSubmit}
                                id="ssoLoginForm"
                                style={{
                                    width: "682px",
                                }}
                            >
                                <Divider variant="fullWidth" sx={{ borderColor: "#D6D6D7" }} />
                                <div style={{ margin: "10px" }}>
                                    {!mainprops.fixedAuthType && (
                                        <Grid item xs={12}>
                                            <Field
                                                name="authType"
                                                as={OZSelect}
                                                label="Select Category "
                                                placeholder="Select"
                                                options={["SAML", "LDAP", "OAUTH2"]}
                                            />
                                        </Grid>
                                    )}
                                </div>
                                <div style={{ margin: "10px", backgroundColor: "#F6F8F9" }}>
                                    {values.authType === "SAML" &&
                                        <div style={{ padding: "15px" }}>
                                            <Grid item xs={12}>
                                                <Field
                                                    name='authConfig'
                                                    as={OZInput}
                                                    label='Identity provider entity ID (URL)'
                                                    placeholder="Enter ID"
                                                />
                                            </Grid>
                                            <Divider style={{ marginTop: "15px", marginBottom: "15px", marginLeft: "-15px", marginRight: "-15px" }}></Divider>
                                            <Grid item xs={12} >
                                                <Field
                                                    name='federationUrl'
                                                    as={OZInput}
                                                    label='Federation URL'
                                                    placeholder="Enter Federation URL"
                                                />
                                            </Grid>
                                            <div style={{ display: "flex", alignItems: "center", fontSize: "12px", fontWeight: 500, color: "#909DAD", marginTop: "10px", marginBottom: "10px", justifyContent: "center" }}>
                                                OR
                                            </div>
                                            <Grid item xs={12}>
                                                <div style={{ marginBottom: '4px', fontSize: "12px", fontWeight: 400, color: "#47586E" }}>
                                                    Config file to import (XML)
                                                </div>
                                                <DropzoneAreaBase
                                                    Icon={UploadIcon}
                                                    initialFiles={values.authFile}
                                                    fileObjects={values.authFile}
                                                    dropzoneParagraphClass={values.authFile.length > 0 ? "config-dropzone-no-text" : "config-dropzone-text"}
                                                    dropzoneClass="config-drop-zone-area"
                                                    previewGridProps={{
                                                        item: {
                                                            position: 'absolute',
                                                            display: 'flex',
                                                            flexWrap: "wrap",
                                                            alignItems: 'end',
                                                            marginTop: "-50px",
                                                        },
                                                        container: { spacing: 1, direction: 'row' }
                                                    }}
                                                    acceptedFiles={['application/xml', 'text/xml']}
                                                    onAlert={(message, variant) => { checkVariant(message, variant); }}
                                                    onDelete={() => { setFieldValue('authFile', []); }}
                                                    onAdd={(files) => {
                                                        if (files?.length > 0) {
                                                            setFieldValue('authFile', [files[0]]);
                                                        }
                                                    }}
                                                    filesLimit={1}
                                                    useChipsForPreview={true}
                                                    showAlerts={false}
                                                />
                                            </Grid>
                                        </div>
                                    }
                                    {values.authType === "LDAP" &&
                                        <div style={{ padding: "15px", display: 'flex', flexDirection: "column", justifyContent: 'space-between', gap: "10px" }}>
                                            <Grid item xs={12}>
                                                <Field
                                                    name='url'
                                                    as={OZInput}
                                                    label='Connection URL'
                                                    placeholder="Enter Connection URL"
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Field
                                                    name='port'
                                                    as={OZInput}
                                                    label='Connection Port'
                                                    placeholder="Enter Connection Port"
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Field
                                                    name='userDn'
                                                    as={OZInput}
                                                    label='User DN'
                                                    placeholder="Enter User DN"
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Field
                                                    name='domain'
                                                    as={OZInput}
                                                    label='Domain'
                                                    placeholder="Enter Domain"
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Field
                                                    name='dc'
                                                    as={OZInput}
                                                    label='DC'
                                                    placeholder="Enter DN"
                                                />
                                            </Grid>
                                        </div>
                                    }
                                    {values.authType === "OAUTH2" &&
                                        <div style={{ padding: "15px", display: 'flex', flexDirection: "column", justifyContent: 'space-between', gap: "10px" }}>
                                            <Grid item xs={12}>
                                                <Field name='clientId' as={OZInput} label='Client ID' placeholder='Enter Client ID' />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Field name='clientSecret' as={OZInput} allowUnsafeInput label='Client Secret' placeholder='Enter Client Secret' />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <ScopeChipInput
                                                    value={values.scope}
                                                    onChange={(nextScopes) => setFieldValue('scope', nextScopes)}
                                                    onBlur={() => setFieldTouched('scope', true)}
                                                    error={touched.scope && Boolean(errors.scope)}
                                                    helperText={touched.scope && errors.scope}
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Field name='authorizationUri' as={OZInput} fieldType='url' label='Authorization URI' placeholder='Enter Authorization URI' />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Field name='tokenUri' as={OZInput} fieldType='url' label='Token URI' placeholder='Enter Token URI' />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Field name='userInfoUri' as={OZInput} fieldType='url' label='User Info URI' placeholder='Enter User Info URI' />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Field name='userNameAttributeName' as={OZInput} label='Username Attribute Name' placeholder='Enter Username Attribute Name' />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Field name='jwkSetUri' as={OZInput} fieldType='url' label='JWK Set URI' placeholder='Enter JWK Set URI' />
                                            </Grid>
                                        </div>
                                    }
                                    {values.authType !== "OAUTH2" &&
                                        <div style={{ padding: "15px", display: 'flex', flexDirection: "column", justifyContent: 'space-between', gap: "10px" }}>
                                            <Field
                                                name='isAllowAdmin'
                                                as={OZFCheckbox}
                                                label='Disable Admin Login'
                                                labelPosition='end'
                                            />
                                        </div>
                                    }
                                </div>
                            </form>
                        </div>
                    )}
                </Formik>
            </div>
        )
    }

    return (
        <>
            {loading &&
                <div style={{ position: 'fixed', top: '0', left: '0', width: '100%', height: '100%', backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1400 }} >
                    <LoadingScreen />
                </div>
            }
            <OZDialog open={mainprops.open} onClose={mainprops.onClickCancel} title="Single sign-on" hideSaveButton={loading} saveButtonProps={{ form: 'ssoLoginForm', type: 'submit' }} form="ssoLoginForm">
                {DataForm()}
            </OZDialog>
        </>
    )
}

export default SSOLogin;
